import React from "react";
import { Link } from "react-router-dom";
import { PROFILE_CARD } from "@/constants/testIds";
import { MapPin, Briefcase, BadgeCheck } from "lucide-react";

const FALLBACK = "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&q=80";

export default function ProfileCard({ profile, onShortlist, onInterest }) {
    const photo = profile.photos?.[0] || FALLBACK;
    return (
        <div className="group bg-white border border-navy-100 rounded-2xl overflow-hidden shadow-[0_8px_30px_rgba(15,23,42,0.04)] hover:shadow-[0_20px_40px_rgba(15,23,42,0.08)] hover:-translate-y-1 transition-all duration-300 ease-out">
            <Link to={`/profile/${profile.user_id}`} data-testid={PROFILE_CARD.view} className="block relative aspect-[4/5] overflow-hidden bg-linen">
                <img
                    src={photo.startsWith("http") ? photo : `${process.env.REACT_APP_BACKEND_URL}${photo}`}
                    alt={profile.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    onError={(e) => { e.currentTarget.src = FALLBACK; }}
                />
                {profile.verified && (
                    <div className="absolute top-3 right-3 bg-white/95 backdrop-blur-sm rounded-lg px-2.5 py-1 flex items-center gap-1 shadow-sm">
                        <BadgeCheck className="h-3.5 w-3.5 text-gold-600" strokeWidth={2} />
                        <span className="text-[10px] tracking-wider uppercase font-semibold text-navy">Verified</span>
                    </div>
                )}
                {profile.premium && (
                    <div className="absolute top-3 left-3 bg-gold text-navy rounded-lg px-2.5 py-1 text-[10px] tracking-wider uppercase font-semibold shadow-md">
                        Premium
                    </div>
                )}
            </Link>
            <div className="p-5">
                <Link to={`/profile/${profile.user_id}`} className="block">
                    <h3 className="font-serif text-2xl text-navy leading-tight">{profile.full_name || profile.name}</h3>
                    <div className="mt-1 text-sm text-navy-500 font-sans">
                        {profile.age ? `${profile.age} yrs` : ""}
                        {profile.age && profile.height_cm ? " • " : ""}
                        {profile.height_cm ? `${Math.floor(profile.height_cm/30.48)}'${Math.round((profile.height_cm/2.54)%12)}"` : ""}
                    </div>
                </Link>
                <div className="mt-3 space-y-1.5 text-sm font-sans text-navy-500">
                    {profile.profession && (
                        <div className="flex items-center gap-2">
                            <Briefcase className="h-3.5 w-3.5 text-gold-600" />
                            <span className="truncate">{profile.profession}</span>
                        </div>
                    )}
                    {(profile.city || profile.country) && (
                        <div className="flex items-center gap-2">
                            <MapPin className="h-3.5 w-3.5 text-gold-600" />
                            <span className="truncate">{[profile.city, profile.country].filter(Boolean).join(", ")}</span>
                        </div>
                    )}
                    {profile.denomination && (
                        <div className="text-xs uppercase tracking-wider text-navy-400 pt-1">{profile.denomination}</div>
                    )}
                </div>
                {(onShortlist || onInterest) && (
                    <div className="mt-4 flex gap-2">
                        {onShortlist && (
                            <button
                                data-testid={PROFILE_CARD.shortlist}
                                onClick={() => onShortlist(profile)}
                                className="flex-1 text-xs tracking-wider uppercase font-medium border border-navy-200 text-navy-600 hover:border-navy hover:text-navy rounded-full py-2 transition-all active:scale-95"
                            >
                                Shortlist
                            </button>
                        )}
                        {onInterest && (
                            <button
                                data-testid={PROFILE_CARD.interest}
                                onClick={() => onInterest(profile)}
                                className="flex-1 text-xs tracking-wider uppercase font-medium bg-gold text-navy hover:bg-gold-600 hover:text-white rounded-full py-2 transition-all active:scale-95 shadow-sm shadow-gold/20"
                            >
                                Interest
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
